import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ 
              position: 'fixed', 
              inset: 0, 
              background: 'rgba(0,0,0,0.5)', 
              backdropFilter: 'blur(4px)',
              zIndex: 10000 
            }}
          />
          <motion.div 
            initial={{ x: '100%', opacity: 0.5 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.5 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxWidth: '500px',
              background: 'var(--bg-dark)',
              borderLeft: '1px solid var(--border-light)',
              boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
              zIndex: 10001,
              overflowY: 'auto',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              color: 'var(--text-primary)'
            }}
            data-lenis-prevent
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', background: 'linear-gradient(to right, var(--accent-teal), var(--accent-purple))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {t('privacy.title')}
              </h2>
              <button
                onClick={onClose}
                aria-label={t('privacy.close', 'Cerrar')}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#fff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem'
                }}
              >
                &times;
              </button>
            </div>
            
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '2rem' }}>
              {t('privacy.last_updated')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', lineHeight: 1.6, fontSize: '0.95rem' }}>
              <section>
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>{t('privacy.sec1_title')}</h3>
                <p>{t('privacy.sec1_p1')}</p>
                <p>{t('privacy.sec1_p2')}</p>
              </section>

              <section>
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>{t('privacy.sec2_title')}</h3>
                <p>{t('privacy.sec2_p1')}</p>
                <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.sec2_l1') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.sec2_l2') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.sec2_l3') }} />
                </ul>
              </section>

              <section>
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>{t('privacy.sec3_title')}</h3>
                <p>{t('privacy.sec3_p1')}</p>
                <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <li>{t('privacy.sec3_l1')}</li>
                  <li>{t('privacy.sec3_l2')}</li>
                  <li>{t('privacy.sec3_l3')}</li>
                  <li>{t('privacy.sec3_l4')}</li>
                </ul>
                <p style={{ marginTop: '0.5rem' }} dangerouslySetInnerHTML={{ __html: t('privacy.sec3_p2') }} />
              </section>

              <section>
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>{t('privacy.sec4_title')}</h3>
                <p>{t('privacy.sec4_p1')}</p>
                <p style={{ marginTop: '0.5rem' }}>{t('privacy.sec4_p2')}</p>
              </section>

              <section>
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>{t('privacy.sec5_title')}</h3>
                <p>{t('privacy.sec5_p1')}</p>
                <ul style={{ paddingLeft: '1.5rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.sec5_l1') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.sec5_l2') }} />
                  <li dangerouslySetInnerHTML={{ __html: t('privacy.sec5_l3') }} />
                </ul>
              </section>

              <section>
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>{t('privacy.sec6_title')}</h3>
                <p dangerouslySetInnerHTML={{ __html: t('privacy.sec6_p1') }} />
              </section>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PrivacyModal;
